import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetAudExchangeRates } from '~/hooks/apis/fx/useGetConvertedCostPricesInAud';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import {
  usePackageOptionsContext,
  usePricingOptionNamesContext,
  useScrapeSessionContext,
} from '../../PricingToolSessionContext';
import { OPTION_NAME_PRESETS, PRICING_OPTION_LABELS, optionLabel } from '../../constants';
import { deriveOpportunityCurrency } from '../inclusions/inclusionsModel';
import RawCsvDataTable from '../scrape/RawCsvDataTable';
import { computeDailyDiscountStats } from '../scrape/otaParity/computeGenuineDiscount';
import { computeDailyMarketStats } from '../scrape/otaParity/computeOtaMarketStats';

import CopyFromOptionBanner from './CopyFromOptionBanner';
import LinkRoomToAssetDialog from './LinkRoomToAssetDialog';
import MarginTable from './MarginTable';
import NetRateFormulaDialog from './NetRateFormulaDialog';
import PackageOptionsTable from './PackageOptionsTable';
import PastePreviousOfferDialog from './PastePreviousOfferDialog';
import PricingTable from './PricingTable';
import { BASIC_CATEGORY_INDEX, MAX_LOS, MIN_LOS } from './packageCategories';
import { collectRoomAssets, selectableRoomAssets } from './packageOptionsModel';

type PackageOptionsTabProps = {
  offerId?: string;
};

export default function PackageOptionsTab({ offerId }: PackageOptionsTabProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { parsedCsv, stayInputs } = useScrapeSessionContext();
  const { data: opportunityRelated } = useGetOpportunityRelated(offerId);
  // Net rates are contracted in the account's currency; sell prices are always AUD. One currency
  // covers every room, so it lives here rather than per room type.
  const netCurrency = useMemo(
    () => deriveOpportunityCurrency(opportunityRelated?.deals ?? []),
    [opportunityRelated?.deals],
  );
  const { audToSourceCurrencyRate, isLoadingByCurrency } = useGetAudExchangeRates([netCurrency]);
  const isNetRateLoading = netCurrency !== 'AUD' && (isLoadingByCurrency[netCurrency] ?? false);
  const audToNetRate = audToSourceCurrencyRate[netCurrency] ?? (netCurrency === 'AUD' ? 1 : undefined);
  const [otaComparisonOpen, setOtaComparisonOpen] = useState(false);
  const marketStats = useMemo(
    () => (parsedCsv ? computeDailyMarketStats(parsedCsv, undefined, stayInputs?.blackoutDates) : []),
    [parsedCsv, stayInputs?.blackoutDates],
  );
  const discountStats = useMemo(
    () => (stayInputs ? computeDailyDiscountStats(marketStats, stayInputs) : []),
    [marketStats, stayInputs],
  );

  const {
    activeOption,
    setActiveOption,
    optionState,
    accountAssets,
    addAccountAsset,
    addRoomType,
    copyFromFirstOption,
    deleteRoomType,
    selectAsset,
    toggleAsiaOnly,
    updateRoomPricing,
    setRoomSellPrice,
    setRoomNetRate,
    setNetRateFormula,
    applyNetRateFormula,
    applyPastedOffer,
    setSelectedLos,
    addLos,
    deleteLos,
  } = usePackageOptionsContext();
  const { optionNames, setOptionName } = usePricingOptionNamesContext();
  const { lengthsOfStay, roomsByCategory, selectedLos, pricingByRoomId, netRateFormula } = optionState;

  const [newLos, setNewLos] = useState('');
  const [netRateDialogOpen, setNetRateDialogOpen] = useState(false);
  const [pasteOfferDialogOpen, setPasteOfferDialogOpen] = useState(false);
  const [linkAssetDialogOpen, setLinkAssetDialogOpen] = useState(false);

  const allAssets = useMemo(
    () => collectRoomAssets(opportunityRelated?.deals ?? [], accountAssets),
    [opportunityRelated?.deals, accountAssets],
  );

  const linkableAssets = useMemo(
    () => selectableRoomAssets(allAssets, roomsByCategory[BASIC_CATEGORY_INDEX]),
    [allAssets, roomsByCategory],
  );

  const optionLabels = PRICING_OPTION_LABELS.map((_, index) => optionLabel(optionNames, index));

  const netRateRooms = useMemo(
    () =>
      roomsByCategory
        .flat()
        .filter((room) => room.assetName)
        .map((room) => ({ id: room.id, name: room.assetName })),
    [roomsByCategory],
  );
  // "All room types" should include every room row, linked or not — only the "choose" checklist
  // needs an asset name to display.
  const allRoomIds = useMemo(() => roomsByCategory.flat().map((room) => room.id), [roomsByCategory]);

  function handleAddLos() {
    addLos(Number(newLos));
    setNewLos('');
  }

  return (
    <TabPanelShell
      subtitle="Configure room types, LOS, pricing, and package upgrades across options."
      title="Package Options"
    >
      <Paper variant="outlined">
        <PackageOptionsTable />
      </Paper>

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button disabled={!parsedCsv || !stayInputs} onClick={() => setOtaComparisonOpen(true)} variant="contained">
          OTA Comparison
        </Button>
      </Box>

      {parsedCsv && stayInputs && (
        <RawCsvDataTable
          currencyCode={stayInputs.currencyCode}
          discountStats={discountStats}
          marketStats={marketStats}
          onClose={() => setOtaComparisonOpen(false)}
          open={otaComparisonOpen}
          parsed={parsedCsv}
        />
      )}

      <Paper sx={{ mt: 4, p: 2 }} variant="outlined">
        <Stack spacing={2}>
          <Typography fontWeight="bold" variant="h6">
            Package Options
          </Typography>

          <Box>
            <Typography color="text.secondary" variant="caption" sx={{ display: 'block', mb: 0.75 }}>
              Account assets
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
              {allAssets.filter((asset) => asset.name).length === 0 ? (
                <Typography color="text.secondary" fontStyle="italic" variant="caption">
                  No assets yet — created when adding a room type
                </Typography>
              ) : (
                allAssets
                  .filter((asset) => asset.name)
                  .map((asset) => <Chip key={asset.sfid ?? asset.name} label={asset.name} size="small" />)
              )}
            </Stack>
          </Box>

          <Divider />

          <ToggleButtonGroup
            exclusive
            onChange={(_event, next) => next !== null && setActiveOption(next)}
            size="small"
            value={activeOption}
          >
            {optionLabels.map((label, index) => (
              <ToggleButton key={index} value={index}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {activeOption !== 0 && (
            <CopyFromOptionBanner
              onCopy={copyFromFirstOption}
              sourceOptionLabel={optionLabels[0]}
              targetOptionLabel={optionLabels[activeOption]}
            />
          )}

          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={2}>
            <Stack alignItems="center" direction="row" spacing={1}>
              <Typography color="text.secondary" variant="body2">
                Option name:
              </Typography>
              <Select
                displayEmpty
                onChange={(e) => setOptionName(activeOption, e.target.value === '' ? undefined : e.target.value)}
                size="small"
                sx={{ minWidth: 160 }}
                value={optionNames[activeOption] ?? ''}
              >
                <MenuItem value="">{PRICING_OPTION_LABELS[activeOption]} (default)</MenuItem>
                {OPTION_NAME_PRESETS.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            <Tooltip title={isNetRateLoading ? `Waiting for the ${netCurrency} exchange rate to load…` : ''}>
              <span>
                <Button
                  disabled={isNetRateLoading}
                  onClick={() => setNetRateDialogOpen(true)}
                  size="small"
                  variant="text"
                >
                  Net Rate formula
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <NetRateFormulaDialog
            allRoomIds={allRoomIds}
            formula={netRateFormula}
            onApply={({ applyToExtraNight, netPercent, roomIds }) => {
              if (audToNetRate === undefined) {
                enqueueSnackbar(`Exchange rate for ${netCurrency} unavailable — net rate not applied`, {
                  variant: 'error',
                });
                return;
              }
              applyNetRateFormula(netPercent, applyToExtraNight, audToNetRate, roomIds);
            }}
            onChangeFormula={setNetRateFormula}
            onClose={() => setNetRateDialogOpen(false)}
            open={netRateDialogOpen}
            rooms={netRateRooms}
          />

          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={1}>
            <Typography color="text.secondary" variant="body2">
              Lengths of stay:
            </Typography>
            {lengthsOfStay.map((nights) => (
              <Chip
                key={nights}
                label={`${nights}N`}
                onDelete={lengthsOfStay.length > 1 ? () => deleteLos(nights) : undefined}
                size="small"
              />
            ))}
            <TextField
              inputProps={{ max: MAX_LOS, min: MIN_LOS }}
              onChange={(e) => setNewLos(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="night:"
              size="small"
              sx={{ width: 90 }}
              type="number"
              value={newLos}
            />
            <Button disabled={!newLos} onClick={handleAddLos} size="small" variant="outlined">
              + Add LOS
            </Button>
          </Stack>

          <PricingTable
            assets={allAssets}
            netCurrency={netCurrency}
            onDeleteRoomType={deleteRoomType}
            onSelectAsset={selectAsset}
            onSelectedLosChange={setSelectedLos}
            onToggleAsiaOnly={(categoryIndex, id) => {
              const room = roomsByCategory[categoryIndex]?.find((candidate) => candidate.id === id);
              const enabling = room !== undefined && !room.asiaOnly;
              const refusal = toggleAsiaOnly(categoryIndex, id);
              if (refusal) {
                enqueueSnackbar(refusal, { variant: 'warning' });
              } else if (enabling) {
                enqueueSnackbar(`Asia only enabled for ${room.assetName}`, { variant: 'info' });
              }
            }}
            onUpdateRoomPricing={updateRoomPricing}
            onSetRoomSellPrice={setRoomSellPrice}
            onSetRoomNetRate={setRoomNetRate}
            pricingByRoomId={pricingByRoomId}
            roomsByCategory={roomsByCategory}
            selectedLos={selectedLos}
            sortedLos={lengthsOfStay}
          />

          <MarginTable
            audToSourceCurrencyRate={audToSourceCurrencyRate}
            netCurrency={netCurrency}
            pricingByRoomId={pricingByRoomId}
            roomsByCategory={roomsByCategory}
            sortedLos={lengthsOfStay}
          />

          <Stack direction="row" spacing={2}>
            <Button onClick={() => setLinkAssetDialogOpen(true)} startIcon="+" variant="outlined">
              Add room type
            </Button>
            <Button onClick={() => setPasteOfferDialogOpen(true)} variant="outlined">
              Paste previous offer
            </Button>
          </Stack>

          <LinkRoomToAssetDialog
            assets={linkableAssets}
            onAddRoom={(asset) => addRoomType(BASIC_CATEGORY_INDEX, asset)}
            onClose={() => setLinkAssetDialogOpen(false)}
            onCreateAsset={addAccountAsset}
            open={linkAssetDialogOpen}
          />

          <PastePreviousOfferDialog
            existingRoomNames={allAssets.map((asset) => asset.name)}
            onApply={(roomRows) => applyPastedOffer(roomRows, allAssets)}
            onClose={() => setPasteOfferDialogOpen(false)}
            open={pasteOfferDialogOpen}
          />
        </Stack>
      </Paper>
    </TabPanelShell>
  );
}
